import {useSportContext} from '@/context/SportContext';
import {
  dbat75SwingsPermittedLocationIds,
  dbatPermittedLocationIds,
  fkoPermittedLocationIds,
  freeTrialClassesPermittedLocationIds,
  freeTrialLeaguesPermittedLocationIds,
  freeTrialPickupSoccerPermittedLocationIds,
  ftcDsrPermittedLocationIds,
  playerAssessmentPermittedLocationIds,
} from '@/feature-flags/fko-locations';
import {useMemo} from 'react';
import {useParams} from 'react-router-dom';

export type SportName =
  | 'dbat'
  | 'playerAssessment'
  | 'classes'
  | 'freeTrialClasses'
  | 'toca'
  | 'dbat75Swings'
  | 'ftcDsr'
  | 'freeTrialPickupSoccer'
  | 'freeTrialLeagues';

export interface SportInfo {
  sport: SportName;
  sessionTypeName: string;
  isDbat: boolean;
  isDbat75Swings: boolean;
  isFtcDsr: boolean;
  isPlayerAssessment: boolean;
  isClasses: boolean;
  isFreeTrialClasses: boolean;
  isFreeTrialPickupSoccer: boolean;
  isFreeTrialLeagues: boolean;
  isFreeBaselineSession: boolean;
  basePath: string;
  basePathWithoutSessionId: string;
  fkoMethod: string; // Needs to match expected HubSpot value
  fkoAnalyticsPrefix: string;
  sportPermittedLocations: string[];
  /* The `applyEligibilityCheck` flag applies to getUserExistsAndEligibility().
   * If it is set to `true`, you must include sessionTypeNames either as direct function params
   * or hardcoded in `getPresetSessionTypeNamesBySport`
   */
  applyEligibilityCheck: boolean;
}

export const useGetSport = (): SportInfo => {
  const {sport} = useSportContext();
  const {sessionId} = useParams();

  const sportInfo: SportInfo = useMemo(() => {
    if (sport === 'DBAT_75_SWINGS') {
      return {
        sport: 'dbat75Swings',
        sessionTypeName: 'DBAT 75 Free Swings',
        isDbat75Swings: true,
        isFreeBaselineSession: false,
        isDbat: false,
        isFtcDsr: false,
        isPlayerAssessment: false,
        isClasses: false,
        isFreeTrialClasses: false,
        isFreeTrialPickupSoccer: false,
        isFreeTrialLeagues: false,
        basePath: 'offer/dbat-75-free-swings-claim',
        basePathWithoutSessionId: 'offer/dbat-75-free-swings-claim',
        fkoMethod: 'player_portal_dbat_75_swings',
        fkoAnalyticsPrefix: 'dbat_75_swings',
        sportPermittedLocations: dbat75SwingsPermittedLocationIds,
        applyEligibilityCheck: false,
      };
    }

    if (sport === 'FTC_DSR') {
      return {
        sport: 'ftcDsr',
        sessionTypeName: 'Free Trial Class',
        isDbat75Swings: false,
        isFtcDsr: true,
        isFreeBaselineSession: false,
        isDbat: false,
        isPlayerAssessment: false,
        isClasses: false,
        isFreeTrialClasses: false,
        isFreeTrialPickupSoccer: false,
        isFreeTrialLeagues: false,
        basePath: '/ftc-dsr',
        basePathWithoutSessionId: '/ftc-dsr',
        fkoMethod: 'player_portal_ftc_dsr',
        fkoAnalyticsPrefix: 'ftc_dsr',
        sportPermittedLocations: ftcDsrPermittedLocationIds,
        applyEligibilityCheck: false,
      };
    }

    if (sport === 'FREE_TRIAL_PICKUP_SOCCER') {
      return {
        sport: 'freeTrialPickupSoccer',
        sessionTypeName: 'Free Trial Class',
        isDbat75Swings: false,
        isFtcDsr: false,
        isFreeBaselineSession: false,
        isDbat: false,
        isPlayerAssessment: false,
        isClasses: false,
        isFreeTrialClasses: false,
        isFreeTrialPickupSoccer: true,
        isFreeTrialLeagues: false,
        basePath: '/ftp',
        basePathWithoutSessionId: '/ftp',
        fkoMethod: 'player_portal_free_trial_pickup_soccer_registration',
        fkoAnalyticsPrefix: 'free_trial_pickup_soccer',
        sportPermittedLocations: freeTrialPickupSoccerPermittedLocationIds,
        applyEligibilityCheck: false,
      };
    }

    if (sport === 'FREE_TRIAL_LEAGUES') {
      return {
        sport: 'freeTrialLeagues',
        sessionTypeName: 'Free Trial Class',
        isDbat75Swings: false,
        isFtcDsr: false,
        isFreeBaselineSession: false,
        isDbat: false,
        isPlayerAssessment: false,
        isClasses: false,
        isFreeTrialClasses: false,
        isFreeTrialPickupSoccer: false,
        isFreeTrialLeagues: true,
        basePath: '/ftl',
        basePathWithoutSessionId: '/ftl',
        fkoMethod: 'player_portal_free_toca_class_registration',
        fkoAnalyticsPrefix: 'free_trial_classes',
        sportPermittedLocations: freeTrialLeaguesPermittedLocationIds,
        applyEligibilityCheck: false,
      };
    }

    if (sport === 'DBAT') {
      return {
        sport: 'dbat',
        sessionTypeName: 'Free Evaluation',
        isDbat75Swings: false,
        isFtcDsr: false,
        isFreeBaselineSession: false,
        isDbat: true,
        isPlayerAssessment: false,
        isClasses: false,
        isFreeTrialClasses: false,
        isFreeTrialPickupSoccer: false,
        isFreeTrialLeagues: false,
        basePath: sessionId ? `/${sessionId}/baseball-fps` : '/baseball-fps',
        basePathWithoutSessionId: '/baseball-fps',
        fkoMethod: 'player_portal_d-bat_promotional_lesson_registration',
        fkoAnalyticsPrefix: 'dbat_fko',
        sportPermittedLocations: dbatPermittedLocationIds,
        applyEligibilityCheck: true,
      };
    }

    if (sport === 'PLAYER_ASSESSMENT') {
      return {
        sport: 'playerAssessment',
        sessionTypeName: 'Player Assessment',
        isDbat75Swings: false,
        isFtcDsr: false,
        isFreeBaselineSession: false,
        isDbat: false,
        isPlayerAssessment: true,
        isClasses: false,
        isFreeTrialClasses: false,
        isFreeTrialPickupSoccer: false,
        isFreeTrialLeagues: false,
        basePath: sessionId ? `/${sessionId}/player-assessment` : '/player-assessment',
        basePathWithoutSessionId: '/player-assessment',
        fkoMethod: 'player_portal_player_assessment_registration',
        fkoAnalyticsPrefix: 'player_assessment_fko',
        sportPermittedLocations: playerAssessmentPermittedLocationIds,
        applyEligibilityCheck: false,
      };
    }

    if (sport === 'FREE_TRIAL_CLASS') {
      return {
        sport: 'freeTrialClasses',
        sessionTypeName: 'Free Trial Class',
        isDbat75Swings: false,
        isFtcDsr: false,
        isFreeBaselineSession: false,
        isDbat: false,
        isPlayerAssessment: false,
        isClasses: false,
        isFreeTrialClasses: true,
        isFreeTrialPickupSoccer: false,
        isFreeTrialLeagues: false,
        basePath: sessionId ? `/${sessionId}/ftc` : '/ftc',
        basePathWithoutSessionId: '/ftc',
        fkoMethod: 'player_portal_free_toca_class_registration',
        fkoAnalyticsPrefix: 'free_trial_classes',
        sportPermittedLocations: freeTrialClassesPermittedLocationIds,
        applyEligibilityCheck: true, // TODO this param currently does nothing for getFreeTrialClassProfileAndEligibilityInfo
      };
    }

    // TODO CLASSES we probably need to support both free trial and regular classes at some point
    // if (sport === 'CLASSES') {
    //   return {
    //     sport: 'classes',
    //     sessionTypeName: 'Free Trial Class', // TODO
    //     isDbat: false,
    //     isPlayerAssessment: false,
    //     isClasses: true,
    //     isFreeTrialClasses: false,
    //     basePath: sessionId ? `/${sessionId}/classes` : '/classes',
    //     basePathWithoutSessionId: '/classes',
    //     fkoMethod: 'player_portal_free_toca_class_registration', // TODO
    //     fkoAnalyticsPrefix: 'free_trial_classes', // TODO
    //     sportPermittedLocations: freeTrialClassesPermittedLocationIds, // TODO
    //   };
    // }

    return {
      sport: 'toca',
      sessionTypeName: 'Free Baseline Session',
      isDbat75Swings: false,
      isFtcDsr: false,
      isFreeBaselineSession: true,
      isDbat: false,
      isPlayerAssessment: false,
      isClasses: false,
      isFreeTrialClasses: false,
      isFreeTrialPickupSoccer: false,
      isFreeTrialLeagues: false,
      basePath: sessionId ? `/${sessionId}/fko` : '/fko',
      basePathWithoutSessionId: '/fko',
      fkoMethod: 'player_portal_fko_registration',
      fkoAnalyticsPrefix: 'fko',
      sportPermittedLocations: fkoPermittedLocationIds,
      applyEligibilityCheck: true,
    };
  }, [sessionId, sport]);

  return sportInfo;
};
